import React, { useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { selectedFlowchartIdSelector } from "src/EstateFlowchart/slices/EstateFlowChartSlice";
import { selectReleaseToggles } from "src/ReleaseToggles/releaseTogglesSlice";
import { RouteWithId } from "../../../routes/types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
    navItemSelector,
    setActiveNavItem,
    setSaveClicked,
    visitedNavsSelector,
} from "../../slices/NavigationSlice";
import { scrollToFormSection, scrollToTop } from "../../utils/FormContentUtils";
import "./_form-nav.scss";
import { getNavItemPage, getSectionId, replaceVariables } from "./FormNavUtils";
import { NavItem } from "./NavItem";
import { NavItemType } from "./NavItemType";

interface NavigationPaneProps {
    navConfig: NavItem[];
    estateItemId: string | undefined;
    validateForm: () => boolean;
    saveForm: (exitForm: boolean) => Promise<boolean>;
};

export const addScrollReleaseListener = (listener: () => void, scrollableElem: HTMLElement | null) => {
    if (scrollableElem) {
        scrollableElem.addEventListener("scroll", listener, { once: true });
    } else {
        window.addEventListener("scroll", listener, { once: true });
    }
};

export const removeScrollReleaseListener = (listener: () => void, scrollableElem: HTMLElement | null) => {
    if (scrollableElem) {
        scrollableElem.removeEventListener("scroll", listener);
    } else {
        window.removeEventListener("scroll", listener);
    }
};

const FormNav = ({ navConfig, estateItemId, validateForm, saveForm }: NavigationPaneProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const selectedFlowchartId = useAppSelector(selectedFlowchartIdSelector);
    const history = useHistory();
    const activeNavItem = useAppSelector(navItemSelector);
    const visitedNavItems = useAppSelector(visitedNavsSelector);
    const {id} = useParams<RouteWithId>();
    const scrollHandlerRef = useRef<() => void>();
    const isProgrammaticScrollRef = useRef(false);
    // Ref to store timeout for debouncing programmatic scroll end
    const programmaticScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const releaseToggles = useAppSelector(selectReleaseToggles);

    /**
     * Helper to get the current scroll position of the form container or window.
     */
    const getScrollTop = (): number => {
        const scrollableElem = document.getElementById("form-scrollable-Container");
        if (scrollableElem) {
            return scrollableElem.scrollTop;
        } 
        return 0;
    };
    /**
    * Returns true if the scrollable element or window is scrolled to the bottom.
    */
    const isAtScrollBottom = (): boolean => {
        const scrollableElem = document.getElementById("form-scrollable-Container");
        if (scrollableElem) {
            const scrollLocation = scrollableElem.scrollHeight - scrollableElem.scrollTop - scrollableElem.clientHeight
            return (
                scrollLocation < 2
            );
        } 
        return false;
    };

    /**
     * Finds the closest visible nav section to the top of the viewport.
     * @returns The closest NavItem or null if none found.
     */
    const findClosestSection = (): NavItem | null => {
        // If at the very top, return the current page nav item
        const scrollTop = getScrollTop();
        if (scrollTop <= 0) {
            return getCurrentPage() ?? null;
        }
        let closestSection: NavItem | null = null;
        if (isAtScrollBottom()) {
            return getCurrentPage()?.children?.slice(-1)[0] ?? null;
        }
        navConfig.forEach((navPage, pageIndex) => {
            navPage.children?.forEach((navLink, linkIndex) => {
                const sectionId = getSectionId(pageIndex, linkIndex);
                const element = document.getElementById(sectionId);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top < 350 && rect.bottom > 550) {
                        closestSection = navLink;
                    }
                }
            });
        });
        return closestSection;
    };
    /**
     * Handles scroll event to update active nav item based on scroll position.
     */
    const handleScroll = (): void => {
        if (isProgrammaticScrollRef.current) return; // Ignore programmatic scrolls
        const closestSection = findClosestSection();
        if (closestSection) {
            dispatch(setActiveNavItem(closestSection));
        }
    };
    /**
     * Schedules isProgrammaticScrollRef.current = false after scroll ends (debounced)
     */
    const scheduleProgrammaticScrollEnd = () => {
        if (programmaticScrollTimeout.current) {
            clearTimeout(programmaticScrollTimeout.current);
        }
        programmaticScrollTimeout.current = setTimeout(() => {
            isProgrammaticScrollRef.current = false;
        }, 150); // 150ms after last scroll event
    };

    const getPath = (navItem: NavItem): string => {
        const pathTemplate = getNavItemPage(navItem)?.pathTemplate ?? "";
        return replaceVariables(pathTemplate, {id, flowchartId: selectedFlowchartId, estateItemId});
    }

    /**
     * Handles updating the URL when the active nav item changes.
     */
    useEffect(() => {
        if (!activeNavItem || !estateItemId) return;
        const currentPage = getCurrentPage();
        const isChildOfCurrentPageOrCurrentPage = activeNavItem.parent?.index === currentPage?.index || activeNavItem.id === currentPage?.id;
        if (!isChildOfCurrentPageOrCurrentPage) {
            history.push(getPath(activeNavItem));
        }
    }, [activeNavItem, id, selectedFlowchartId, estateItemId]);

    /**
     * Handles scroll highlighting effect with scroll event listener.
     */
    useEffect(() => {
        if (releaseToggles?.enableEstateFlowchartMVPRelease) {
            scrollHandlerRef.current = handleScroll;
            const scrollable = document.getElementById("form-scrollable-Container") || window;
            scrollable.addEventListener("scroll", handleScroll);
            // Listen for scroll events to end programmatic scroll
            const onScroll = () => {
                if (isProgrammaticScrollRef.current) {
                    scheduleProgrammaticScrollEnd();
                }
            };
            scrollable.addEventListener("scroll", onScroll);
            return () => {
                scrollable.removeEventListener("scroll", handleScroll);
                scrollable.removeEventListener("scroll", onScroll);
                if (programmaticScrollTimeout.current) {
                    clearTimeout(programmaticScrollTimeout.current);
                }
            };
        }
    }, [navConfig, dispatch, releaseToggles?.enableEstateFlowchartMVPRelease]);
    /**
     * Returns the current page NavItem, or its parent if the active item is a link.
     */
    const getCurrentPage = (): NavItem | undefined => {
        const currentPage: NavItem | undefined = navConfig.find(page => {
            const currentSlug = history.location.pathname.split("/").pop();
            const pageSlug = getPath(page).split("/").pop();
            return currentSlug === pageSlug;
        });
        return currentPage;
    };

    /**
     * Handles navigation to a page in the nav. Validates and saves form if needed.
     * @param pageIndex Index of the page to navigate to
     */
    const handlePageClick = (pageIndex: number): void => {
        const currentPageIndex = getCurrentPage()?.index ?? 0;
        if (!canNavigateTo(pageIndex, currentPageIndex)) {
            dispatch(setSaveClicked(true));
            return;
        }
        if(pageIndex === currentPageIndex) {
            isProgrammaticScrollRef.current = true;
            scrollToTop("smooth");
            scheduleProgrammaticScrollEnd();
            dispatch(setActiveNavItem(navConfig.at(pageIndex)));
            return;
        }
        saveForm(false).then(() => {
            dispatch(setSaveClicked(false));
            dispatch(setActiveNavItem(navConfig.at(pageIndex)));
            isProgrammaticScrollRef.current = true;
            const scrollableElem = document.getElementById("form-scrollable-Container");
            const releaseProgrammaticScroll = () => {
                isProgrammaticScrollRef.current = false;
                removeScrollReleaseListener(releaseProgrammaticScroll, scrollableElem);
            };
            if (getScrollTop() === 0) {
                isProgrammaticScrollRef.current = false;
            } else {
                addScrollReleaseListener(releaseProgrammaticScroll, scrollableElem);
            }
            scrollToTop("auto");
        });
    };

    /**
     * Handles navigation to a link within a page. Validates if needed.
     * @param pageIndex Index of the page
     * @param linkIndex Index of the link
     * @param linkKey DOM key for the link section
     */
    const handleLinkClick = (pageIndex: number, linkIndex: number, linkKey: string): void => {
        const currentPageIndex = getCurrentPage()?.index ?? 0;
        if (!canNavigateTo(pageIndex, currentPageIndex)) {
            dispatch(setSaveClicked(true));
            return;
        }
        const page = navConfig.at(pageIndex);
        const link = page?.children?.at(linkIndex);
        const delay = pageIndex === currentPageIndex ? 100 : 500;
        dispatch(setActiveNavItem(link));
        isProgrammaticScrollRef.current = true;
        setTimeout(() => {
            scrollToFormSection(linkKey);
            scheduleProgrammaticScrollEnd();
        }, delay);
    };

    /**
     * Determines if navigation to a new page or link is allowed. Form must be valid before moving forward.
     * @param targetIndex Index of the target page
     * @param currentIndex Index of the current page
     * @returns True if navigation is allowed
     */
    const canNavigateTo = (targetIndex: number, currentIndex: number): boolean => {
        return targetIndex > currentIndex ? validateForm() : true;
    };

    const generatePageItems = (listItems: JSX.Element[]): JSX.Element[] => {
        navConfig.forEach((navPage, pageIndex) => {
            const pageItemKey = `page${pageIndex}`;
            let pageClassName = activeNavItem?.type === NavItemType.PAGE &&
            activeNavItem.index === pageIndex ?
                "form-nav-page-item active-nav-item" : "form-nav-page-item";

            if ((navPage.children && navPage.children?.length > 0)
                || visitedNavItems.some(nav => nav.id === navPage.id)) {
                pageClassName += " reviewed-nav-item";
            }

            listItems.push(
                <li key={pageItemKey}
                    id={pageItemKey}
                    role="presentation"
                    className={pageClassName}
                    data-testid={"form-nav-page-item"}
                    onClick={() => handlePageClick(pageIndex)}>
                    {navPage.displayName}
                </li>
            );

            generateLinkItems(listItems, navPage, pageItemKey, pageIndex);
        })
        return listItems;
    }

    const generateLinkItems = (listItems: JSX.Element[], navPage: NavItem, pageItemKey: string, pageIndex: number): JSX.Element[] => {
        navPage.children?.forEach((navJumpLink, linkIndex) => {
            const linkItemId = `link${linkIndex}`;
            const linkItemKey = `${pageItemKey}-${linkItemId}`

            const linkClassName = activeNavItem?.type === NavItemType.LINK &&
            activeNavItem.index === linkIndex &&
            activeNavItem.parent?.index === pageIndex ? "form-nav-link-item active-nav-item" : "form-nav-link-item  reviewed-nav-item";

            listItems.push(
                <li key={linkItemKey}
                    id={linkItemKey}
                    role="presentation"
                    className={linkClassName}
                    data-testid={"form-nav-link-item"}
                    onClick={() => handleLinkClick(pageIndex, linkIndex, linkItemKey)}>
                    {navJumpLink.displayName}
                </li>
            );
        })
        return listItems;
    }

    return (
        <div className="form-nav-container">
            <div className="form-nav-content">
                <ul className="form-nav-page-list" data-testid="form-nav-page-list">
                    {generatePageItems([])}
                </ul>
            </div>
        </div>
    )
};

export default FormNav;